import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, Download, Eye, Send, Save, Trash2, 
  AlertTriangle, CheckCircle, Search, Filter, ArrowLeft,
  ShoppingCart, Calendar, User, DollarSign, Package, Clock,
  Printer, Copy, Check, Share2, FileSignature, FileCheck, 
  FileX, FilePlus, Briefcase, CreditCard, Truck, Globe, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useDocuments } from '../../hooks/useDocuments';
import { supabase } from '../../lib/supabase';
import { jsPDF } from 'jspdf';
import { useEmail } from '../../hooks/useEmail';
import 'jspdf-autotable';

interface Order {
  id: string;
  order_number: string;
  total_amount: number;
  status: string;
  created_at: string;
  user_id: string;
  buyer?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  items?: OrderItem[];
}

interface OrderItem {
  id: string;
  product_id: string;
  quantity: number;
  price: number;
  total_price: number;
  product: {
    name: string;
    sku: string;
  };
}

interface DocumentTemplate {
  id: string;
  name: string;
  type: string;
  icon: React.ElementType;
  description: string;
}

const SellerDocumentPreparationPage = () => {
  const { user } = useSupabase();
  const { documents, loading, createDocument, updateVisibilityToBuyer, updateSavedStatus, deleteDocument } = useDocuments();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [documentType, setDocumentType] = useState('Commercial Invoice');
  const [generatingDocument, setGeneratingDocument] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [error, setError] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [supplierInfo, setSupplierInfo] = useState<any>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [documentCopied, setDocumentCopied] = useState(false);
  
  const { sendDocumentNotification } = useEmail();
  const pdfRef = useRef(null);

  // Document templates
  const documentTemplates: DocumentTemplate[] = [
    { 
      id: 'commercial-invoice', 
      name: 'Commercial Invoice', 
      type: 'Commercial Invoice',
      icon: FileText,
      description: 'Official invoice for international trade showing prices and quantities'
    },
    { 
      id: 'packing-list', 
      name: 'Packing List', 
      type: 'Packing List',
      icon: Package,
      description: 'Detailed list of items in a shipment with quantities and packaging details'
    },
    { 
      id: 'proforma-invoice', 
      name: 'Proforma Invoice', 
      type: 'Proforma Invoice',
      icon: FileSignature,
      description: 'Preliminary invoice with estimated costs before final purchase'
    },
    { 
      id: 'certificate-origin', 
      name: 'Certificate of Origin', 
      type: 'Certificate of Origin',
      icon: FileCheck,
      description: 'Document certifying the country of origin of the goods'
    },
    { 
      id: 'bill-of-lading', 
      name: 'Bill of Lading', 
      type: 'Bill of Lading',
      icon: Truck,
      description: 'Transport document for shipping goods internationally'
    },
    { 
      id: 'export-permit', 
      name: 'Export Permit', 
      type: 'Export Permit',
      icon: Globe,
      description: 'Government authorization to export specific goods'
    },
    { 
      id: 'phytosanitary', 
      name: 'Phytosanitary Certificate', 
      type: 'Phytosanitary Certificate',
      icon: FilePlus,
      description: 'Certificate for agricultural exports confirming they are pest-free'
    },
    { 
      id: 'gsp-certificate', 
      name: 'GSP Certificate', 
      type: 'GSP Certificate',
      icon: Briefcase,
      description: 'Certificate for preferential tariff treatment under GSP'
    },
    { 
      id: 'lc-tt-sheet', 
      name: 'LC/TT Sheet', 
      type: 'LC/TT Sheet',
      icon: CreditCard,
      description: 'Payment instructions for Letter of Credit or Telegraphic Transfer'
    }
  ];

  // Fetch seller's orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) return;
      
      try {
        setLoadingOrders(true);
        setError(null);
        
        // Get supplier ID for the current user - use maybeSingle() to handle no results
        const { data: supplierData, error: supplierError } = await supabase
          .from('suppliers')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (supplierError) throw supplierError;
        
        // If no supplier found for this user, set empty orders and return
        if (!supplierData) {
          setOrders([]);
          setLoadingOrders(false);
          setError('No supplier profile found for this user. Please create a supplier profile first.');
          return;
        }
        
        // Store supplier info for document generation
        setSupplierInfo(supplierData);
        
        // Get orders for products from this supplier
        const { data: orderItemsData, error: orderItemsError } = await supabase
          .from('order_items')
          .select(`
            order_id,
            product:product_id(supplier_id)
          `)
          .eq('product.supplier_id', supplierData.id);
        
        if (orderItemsError) throw orderItemsError;
        
        // Extract unique order IDs
        const orderIds = [...new Set(orderItemsData.map(item => item.order_id))];
        
        if (orderIds.length === 0) {
          setOrders([]);
          setLoadingOrders(false);
          return;
        }
        
        // Get order details without joining users to avoid RLS recursion
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select(`
            id,
            order_number,
            total_amount,
            status,
            created_at,
            user_id
          `)
          .in('id', orderIds)
          .order('created_at', { ascending: false });
        
        if (ordersError) throw ordersError;
        
        if (!ordersData || ordersData.length === 0) {
          setOrders([]);
          setLoadingOrders(false);
          return;
        }
        
        // Get unique user IDs from orders
        const userIds = [...new Set(ordersData.map(order => order.user_id).filter(Boolean))];
        
        // Fetch buyer details separately to avoid RLS recursion
        const { data: buyersData, error: buyersError } = await supabase
          .from('users')
          .select('id, first_name, last_name, email')
          .in('id', userIds);
        
        if (buyersError) {
          console.warn('Error fetching buyer details:', buyersError);
          // Continue without buyer details if there's an error
        }
        
        // Map buyer details to orders
        const ordersWithBuyers = ordersData.map(order => ({
          ...order,
          buyer: buyersData?.find(buyer => buyer.id === order.user_id) || {
            id: order.user_id || '',
            first_name: 'Unknown',
            last_name: 'Buyer',
            email: 'N/A'
          }
        }));
        
        setOrders(ordersWithBuyers);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch orders');
      } finally {
        setLoadingOrders(false);
      }
    };
    
    fetchOrders();
  }, [user]);

  // Fetch order items when an order is selected
  useEffect(() => {
    const fetchOrderItems = async () => {
      if (!selectedOrder) return;
      
      try {
        const { data, error } = await supabase
          .from('order_items')
          .select(`
            id,
            product_id,
            quantity,
            price,
            total_price,
            product:product_id(
              name,
              sku
            )
          `)
          .eq('order_id', selectedOrder.id);
        
        if (error) throw error;
        
        setOrderItems(data || []);
      } catch (err) {
        console.error('Error fetching order items:', err);
      }
    };
    
    fetchOrderItems();
  }, [selectedOrder]);

  // Filter orders based on search and status
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (order.buyer?.first_name + ' ' + order.buyer?.last_name).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Generate a document
  const handleGenerateDocument = async () => {
    if (!selectedOrder || !selectedOrder.buyer || !supplierInfo) return;
    
    try {
      setGeneratingDocument(true);
      
      // Generate PDF document
      const doc = new jsPDF();
      
      // Set document properties
      doc.setProperties({
        title: `${documentType} - ${selectedOrder.order_number}`,
        subject: `${documentType} for order ${selectedOrder.order_number}`,
        author: supplierInfo.organization,
        creator: 'Made in BD'
      });
      
      // Add document header
      doc.setFontSize(22);
      doc.setTextColor(33, 33, 33);
      doc.text(documentType.toUpperCase(), 105, 20, { align: 'center' });
      
      // Add document number and date
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Document #: ${documentType.substring(0, 3).toUpperCase()}-${Date.now().toString().substring(8, 13)}`, 20, 30);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 35);
      doc.text(`Order #: ${selectedOrder.order_number}`, 20, 40);
      
      // Add supplier info
      doc.setFontSize(12);
      doc.setTextColor(33, 33, 33);
      doc.text('Supplier:', 20, 50);
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(supplierInfo.organization, 20, 55);
      doc.text(supplierInfo.address || 'No address provided', 20, 60);
      doc.text(`Email: ${supplierInfo.email || 'N/A'}`, 20, 65);
      doc.text(`Phone: ${supplierInfo.phone || 'N/A'}`, 20, 70);
      
      // Add buyer info
      doc.setFontSize(12);
      doc.setTextColor(33, 33, 33);
      doc.text('Buyer:', 120, 50);
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`${selectedOrder.buyer.first_name} ${selectedOrder.buyer.last_name}`, 120, 55);
      doc.text('Shipping Address: On file', 120, 60);
      doc.text(`Email: ${selectedOrder.buyer.email}`, 120, 65);
      
      // Add order items table
      if (documentType === 'Commercial Invoice' || documentType === 'Proforma Invoice' || documentType === 'Packing List') {
        doc.setFontSize(12);
        doc.setTextColor(33, 33, 33);
        doc.text('Order Items:', 20, 85);
        
        // Create table
        const tableColumn = documentType === 'Packing List' 
          ? ["Item", "SKU", "Quantity", "Package Type", "Dimensions", "Weight"]
          : ["Item", "SKU", "Quantity", "Unit Price", "Total"];
        
        const tableRows = orderItems.map(item => {
          if (documentType === 'Packing List') {
            return [
              item.product.name,
              item.product.sku,
              item.quantity.toString(),
              "Carton",
              "30x20x15 cm",
              `${(item.quantity * 0.5).toFixed(1)} kg`
            ];
          } else {
            return [
              item.product.name,
              item.product.sku,
              item.quantity.toString(),
              `$${item.price.toFixed(2)}`,
              `$${item.total_price.toFixed(2)}`
            ];
          }
        });
        
        // @ts-ignore - jspdf-autotable types
        doc.autoTable({
          head: [tableColumn],
          body: tableRows,
          startY: 90,
          theme: 'grid',
          styles: { fontSize: 9 },
          headStyles: { fillColor: [240, 100, 0] }
        });
        
        // Add totals for invoices
        if (documentType === 'Commercial Invoice' || documentType === 'Proforma Invoice') {
          const finalY = (doc as any).lastAutoTable.finalY || 150;
          
          doc.setFontSize(10);
          doc.text(`Subtotal: $${selectedOrder.total_amount.toFixed(2)}`, 150, finalY + 10, { align: 'right' });
          doc.text(`Shipping: $0.00`, 150, finalY + 15, { align: 'right' });
          doc.text(`Tax: $0.00`, 150, finalY + 20, { align: 'right' });
          
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          doc.text(`Total: $${selectedOrder.total_amount.toFixed(2)}`, 150, finalY + 30, { align: 'right' });
          doc.setFont(undefined, 'normal');
        }
      } else if (documentType === 'Certificate of Origin') {
        // Certificate of Origin specific content
        doc.setFontSize(12);
        doc.text('CERTIFICATE OF ORIGIN', 105, 85, { align: 'center' });
        
        doc.setFontSize(10);
        doc.text('1. Goods consigned from (Exporter):', 20, 95);
        doc.text(supplierInfo.organization, 30, 100);
        doc.text(supplierInfo.address || 'Address on file', 30, 105);
        
        doc.text('2. Goods consigned to (Importer):', 20, 115);
        doc.text(`${selectedOrder.buyer.first_name} ${selectedOrder.buyer.last_name}`, 30, 120);
        doc.text('Address on file', 30, 125);
        
        doc.text('3. Means of transport and route:', 20, 135);
        doc.text('By sea/air from Bangladesh to destination', 30, 140);
        
        doc.text('4. Item number, marks, number and kind of packages, description of goods:', 20, 150);
        
        let yPos = 155;
        orderItems.forEach((item, index) => {
          doc.text(`${index + 1}. ${item.product.name} (${item.quantity} units)`, 30, yPos);
          yPos += 5;
        });
        
        doc.text('5. Origin criterion:', 20, yPos + 10);
        doc.text('Wholly obtained in Bangladesh', 30, yPos + 15);
        
        doc.text('6. Declaration by the exporter:', 20, yPos + 25);
        doc.text('The undersigned hereby declares that the above details and statements are correct.', 30, yPos + 30);
        
        doc.text('Place and date: Dhaka, Bangladesh - ' + new Date().toLocaleDateString(), 30, yPos + 40);
        doc.text('Authorized signature: _______________________', 30, yPos + 50);
      } else if (documentType === 'Bill of Lading') {
        // Bill of Lading specific content
        doc.setFontSize(12);
        doc.text('BILL OF LADING', 105, 85, { align: 'center' });
        
        doc.setFontSize(10);
        doc.text('Shipper/Exporter:', 20, 95);
        doc.text(supplierInfo.organization, 30, 100);
        doc.text(supplierInfo.address || 'Address on file', 30, 105);
        
        doc.text('Consignee:', 20, 115);
        doc.text(`${selectedOrder.buyer.first_name} ${selectedOrder.buyer.last_name}`, 30, 120);
        doc.text('Address on file', 30, 125);
        
        doc.text('Notify Party:', 120, 95);
        doc.text(`${selectedOrder.buyer.first_name} ${selectedOrder.buyer.last_name}`, 130, 100);
        doc.text(`Email: ${selectedOrder.buyer.email}`, 130, 105);
        
        doc.text('Vessel/Flight:', 20, 135);
        doc.text('TBD', 70, 135);
        
        doc.text('Port of Loading:', 20, 145);
        doc.text('Chittagong, Bangladesh', 70, 145);
        
        doc.text('Port of Discharge:', 20, 155);
        doc.text('TBD', 70, 155);
        
        // Create table for goods
        const tableColumn = ["Marks & Numbers", "No. of Pkgs", "Description of Goods", "Gross Weight", "Measurement"];
        
        const tableRows = orderItems.map(item => {
          return [
            item.product.sku,
            item.quantity.toString(),
            item.product.name,
            `${(item.quantity * 0.5).toFixed(1)} kg`,
            "TBD"
          ];
        });
        
        // @ts-ignore - jspdf-autotable types
        doc.autoTable({
          head: [tableColumn],
          body: tableRows,
          startY: 165,
          theme: 'grid',
          styles: { fontSize: 9 },
          headStyles: { fillColor: [240, 100, 0] }
        });
        
        const finalY = (doc as any).lastAutoTable.finalY || 200;
        
        doc.text('Freight and Charges:', 20, finalY + 10);
        doc.text('AS ARRANGED', 70, finalY + 10);
        
        doc.text('Place and date of issue:', 20, finalY + 20);
        doc.text(`Dhaka, Bangladesh - ${new Date().toLocaleDateString()}`, 70, finalY + 20);
        
        doc.text('Number of original B/L:', 20, finalY + 30);
        doc.text('THREE (3)', 70, finalY + 30);
      } else if (documentType === 'Export Permit') {
        // Export Permit specific content
        doc.setFontSize(14);
        doc.text('EXPORT PERMIT', 105, 85, { align: 'center' });
        
        doc.setFontSize(10);
        doc.text('Permit Number: EP-' + Date.now().toString().substring(5, 13), 20, 95);
        doc.text('Date of Issue: ' + new Date().toLocaleDateString(), 20, 100);
        doc.text('Valid Until: ' + new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString(), 20, 105);
        
        doc.text('Exporter:', 20, 115);
        doc.text(supplierInfo.organization, 30, 120);
        doc.text(supplierInfo.address || 'Address on file', 30, 125);
        doc.text(`Registration No: REG-${supplierInfo.id.substring(0, 8)}`, 30, 130);
        
        doc.text('Consignee:', 20, 140);
        doc.text(`${selectedOrder.buyer.first_name} ${selectedOrder.buyer.last_name}`, 30, 145);
        doc.text('Address on file', 30, 150);
        doc.text(`Order Reference: ${selectedOrder.order_number}`, 30, 155);
        
        doc.text('Description of Goods:', 20, 165);
        
        let yPos = 170;
        orderItems.forEach((item, index) => {
          doc.text(`${index + 1}. ${item.product.name} (${item.quantity} units)`, 30, yPos);
          yPos += 5;
        });
        
        doc.text('Country of Destination:', 20, yPos + 10);
        doc.text('As per shipping instructions', 100, yPos + 10);
        
        doc.text('This permit is issued in accordance with the Export Control Act of Bangladesh.', 20, yPos + 20);
        doc.text('Authorized Signature: _______________________', 20, yPos + 30);
        doc.text('(Export Control Authority)', 20, yPos + 35);
      }
      
      // Add footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Generated by Made in BD - ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
          105, 
          285, 
          { align: 'center' }
        );
      }
      
      // Save the PDF
      const pdfOutput = doc.output('datauristring');
      setPreviewDocument(pdfOutput);
      
      // Create document record
      await createDocument(
        selectedOrder.id,
        selectedOrder.buyer.id,
        documentType,
        pdfOutput
      );
      
      // Reset selection
      setSelectedOrder(null);
      setDocumentType('Commercial Invoice');
      
      // Send notification email to buyer
      try {
        // Get buyer details
        const { data: buyerData, error: buyerError } = await supabase
          .from('users')
          .select('email, first_name, last_name')
          .eq('id', selectedOrder.buyer.id)
          .single();
          
        if (!buyerError && buyerData) {
          // Send document notification email
          const documentLink = `${window.location.origin}/my-documents`;
          await sendDocumentNotification(
            buyerData.email,
            `${buyerData.first_name} ${buyerData.last_name}`,
            documentType,
            selectedOrder.order_number,
            documentLink
          );
        }
      } catch (emailError) {
        console.error('Error sending document notification email:', emailError);
        // Don't set error state as the document was successfully created
      }
      
      setShowPreview(true);
    } catch (err) {
      console.error('Error generating document:', err);
      setError('Failed to generate document. Please try again.');
    } finally {
      setGeneratingDocument(false);
    }
  };

  // Preview document
  const handlePreviewDocument = (documentUrl: string) => {
    setPreviewDocument(documentUrl);
    setShowPreview(true);
  };

  // Handle sharing document with buyer
  const handleShareWithBuyer = async (documentId: string) => {
    try {
      // Update document visibility
      await updateVisibilityToBuyer(documentId, true);
      
      // Find the document
      const document = documents.find(doc => doc.id === documentId);
      if (!document) return;
      
      // Get buyer details
      const { data: buyerData, error: buyerError } = await supabase
        .from('users')
        .select('email, first_name, last_name')
        .eq('id', document.buyer_user_id)
        .single();
        
      if (!buyerError && buyerData) {
       // Send document notification email
       const documentLink = `${window.location.origin}/my-documents`;
       await sendDocumentNotification(
         buyerData.email,
         `${buyerData.first_name} ${buyerData.last_name}`,
         document.document_type,
         document.order?.order_number || 'Unknown',
         documentLink
       );
     }
   } catch (error) {
     console.error('Error sharing document with buyer:', error);
     setError('Failed to share document with buyer. Please try again.');
    }
  };

  // Handle saving document to seller account
  const handleSaveToAccount = async (documentId: string) => {
    await updateSavedStatus(documentId, true);
  };

  // Handle deleting document
  const handleDeleteDocument = async (documentId: string) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      await deleteDocument(documentId);
    }
  };

  // Print document
  const handlePrintDocument = () => {
    if (previewDocument) {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = previewDocument;
      document.body.appendChild(iframe);
      
      iframe.onload = () => {
        iframe.contentWindow?.print();
        document.body.removeChild(iframe);
      };
    }
  };

  // Copy document link
  const handleCopyDocumentLink = () => {
    if (previewDocument) {
      navigator.clipboard.writeText(previewDocument);
      setDocumentCopied(true);
      setTimeout(() => setDocumentCopied(false), 2000);
    }
  };

  // Get status badge for orders
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </span>;
      case 'processing':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Package className="h-3 w-3 mr-1" />
          Processing
        </span>;
      case 'shipped':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <Send className="h-3 w-3 mr-1" />
          Shipped
        </span>;
      case 'delivered':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Delivered
        </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Cancelled
        </span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/seller-central" className="mr-4 text-gray-500 hover:text-gray-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Document Preparation</h1>
                <p className="text-gray-600">Generate and manage documents for your orders</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
              <div className="text-red-800">
                <h3 className="font-medium">Error</h3>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Document Generation Form */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Generate New Document</h2>
              
              {/* Order Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Order
                </label>
                <div className="relative">
                  <select
                    value={selectedOrder?.id || ''}
                    onChange={(e) => {
                      const orderId = e.target.value;
                      const order = orders.find(o => o.id === orderId) || null;
                      setSelectedOrder(order);
                    }}
                    disabled={orders.length === 0}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {orders.length === 0 ? '-- No orders available --' : '-- Select an order --'}
                    </option>
                    {orders.map((order) => (
                      <option key={order.id} value={order.id}>
                        {order.order_number} - {order.buyer?.first_name} {order.buyer?.last_name} (${order.total_amount.toLocaleString()})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Document Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {documentTemplates.map((template) => (
                    <div 
                      key={template.id}
                      onClick={() => setDocumentType(template.type)}
                      className={`cursor-pointer border rounded-lg p-3 transition-colors ${
                        documentType === template.type 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-gray-200 hover:border-orange-300 hover:bg-orange-25'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          documentType === template.type 
                            ? 'bg-orange-100 text-orange-600' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          <template.icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-sm font-medium ${
                            documentType === template.type ? 'text-orange-700' : 'text-gray-900'
                          } truncate`}>
                            {template.name}
                          </h3>
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                        {template.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Order Details (if selected) */}
              {selectedOrder && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-3">Order Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start space-x-2">
                      <ShoppingCart className="h-4 w-4 text-gray-500 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Order Number</div>
                        <div className="text-sm text-gray-600">{selectedOrder.order_number}</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Calendar className="h-4 w-4 text-gray-500 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Order Date</div>
                        <div className="text-sm text-gray-600">
                          {new Date(selectedOrder.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <User className="h-4 w-4 text-gray-500 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Customer</div>
                        <div className="text-sm text-gray-600">
                          {selectedOrder.buyer?.first_name} {selectedOrder.buyer?.last_name}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <DollarSign className="h-4 w-4 text-gray-500 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Total Amount</div>
                        <div className="text-sm text-gray-600">${selectedOrder.total_amount.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Order Items */}
                  {orderItems.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Order Items</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {orderItems.map((item) => (
                              <tr key={item.id}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item.product.name}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{item.product.sku}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item.quantity}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">${item.price.toFixed(2)}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">${item.total_price.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Generate Button */}
              <button
                onClick={handleGenerateDocument}
                disabled={!selectedOrder || generatingDocument || orders.length === 0}
                className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {generatingDocument ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Generating Document...</span>
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    <span>Generate Document</span>
                  </>
                )}
              </button>
            </div>

            {/* Generated Documents */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Generated Documents</h2>
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search documents..."
                      className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <select
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="invoice">Invoices</option>
                    <option value="packing">Packing Lists</option>
                    <option value="lading">Bills of Lading</option>
                    <option value="origin">Certificates of Origin</option>
                  </select>
                </div>
              </div>
              
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No documents yet</h3>
                  <p className="text-gray-600 mb-6">
                    Generate your first document by selecting an order and document type above.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {documents.map((document) => (
                    <div key={document.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <FileText className="h-6 w-6 text-orange-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{document.document_type}</h3>
                            <p className="text-sm text-gray-600">
                              Order: {document.order?.order_number || 'Unknown'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Created: {new Date(document.created_at).toLocaleString()}
                            </p>
                            <div className="flex items-center space-x-2 mt-1">
                              {document.is_saved_by_seller && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <Save className="h-3 w-3 mr-1" />
                                  Saved
                                </span>
                              )}
                              {document.is_visible_to_buyer && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <Send className="h-3 w-3 mr-1" />
                                  Shared with Buyer
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handlePreviewDocument(document.document_url)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                            title="View Document"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleSaveToAccount(document.id)}
                            disabled={document.is_saved_by_seller}
                            className={`p-2 rounded-full transition-colors ${
                              document.is_saved_by_seller
                                ? 'text-blue-600 bg-blue-50 cursor-not-allowed'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                            title={document.is_saved_by_seller ? 'Already Saved' : 'Save to Account'}
                          >
                            <Save className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleShareWithBuyer(document.id)}
                            disabled={document.is_visible_to_buyer}
                            className={`p-2 rounded-full transition-colors ${sendingEmail ? 'cursor-wait' : ''} ${
                              document.is_visible_to_buyer
                                ? 'text-green-600 bg-green-50 cursor-not-allowed'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                            title={document.is_visible_to_buyer ? 'Already Shared' : 'Share with Buyer'}
                          >
                            {sendingEmail ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(document.id)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded-full transition-colors"
                            title="Delete Document"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Orders List */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
                <div className="flex items-center space-x-2">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>
              </div>
              
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
              
              {loadingOrders ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {error ? 'Unable to load orders' : 'No orders found'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredOrders.map((order) => (
                    <button
                      key={order.id}
                      onClick={() => setSelectedOrder(order)}
                      className={`w-full text-left p-3 border rounded-lg transition-colors ${
                        selectedOrder?.id === order.id
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <div className="font-medium text-gray-900">{order.order_number}</div>
                        {getStatusBadge(order.status)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {order.buyer?.first_name} {order.buyer?.last_name}
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <div className="text-xs text-gray-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          ${order.total_amount.toLocaleString()}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Document Types Info */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Document Types</h2>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <FileText className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Commercial Invoice</h3>
                    <p className="text-sm text-gray-600">
                      Details the transaction between buyer and seller, including prices and quantities.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Package className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Packing List</h3>
                    <p className="text-sm text-gray-600">
                      Lists the contents of a shipment, including quantities and packaging details.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FileCheck className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Certificate of Origin</h3>
                    <p className="text-sm text-gray-600">
                      Certifies that goods in a shipment are obtained, produced, or manufactured in a particular country.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Truck className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Bill of Lading</h3>
                    <p className="text-sm text-gray-600">
                      A legal document between the shipper and carrier detailing the type, quantity, and destination of goods.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {showPreview && previewDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Document Preview</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrintDocument}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Print Document"
                >
                  <Printer className="h-5 w-5" />
                </button>
                <button
                  onClick={handleCopyDocumentLink}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copy Document Link"
                >
                  {documentCopied ? (
                    <Check className="h-5 w-5 text-green-500" />
                  ) : (
                    <Copy className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Close Preview"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <iframe 
                src={previewDocument} 
                className="w-full h-full border-0"
                ref={pdfRef}
              ></iframe>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <a
                href={previewDocument}
                download={`${documentType.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.pdf`}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Download</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerDocumentPreparationPage;